import type { ApiResponse } from "@/services/api/api.types";
import {
  stepsService,
  type SupportingDocumentItem,
  type SupportingDocumentType,
  type UploadSupportingDocumentsResponse,
} from "@/features/protest/services/steps.service";

const SUPPORTING_LIST_CACHE_TTL_MS = 45_000;

type CachedListEntry = { at: number; response: ApiResponse<SupportingDocumentItem[]> };

const supportingListCache = new Map<string, CachedListEntry>();

function supportingListCacheKey(
  userId: string,
  propertyId: string,
  docType: SupportingDocumentType
): string {
  return `${userId.trim()}::${propertyId.trim()}::${docType}`;
}

/**
 * Clears cached GET results for a property (e.g. after upload/delete or property switch).
 */
export function invalidateSupportingDocumentsGetCacheForProperty(propertyId: string): void {
  const pid = propertyId.trim();
  if (!pid) {
    supportingListCache.clear();
    return;
  }
  const needle = `::${pid}::`;
  for (const k of supportingListCache.keys()) {
    if (k.includes(needle)) supportingListCache.delete(k);
  }
}

/**
 * Web: upload already-selected files (from `<input type="file">`).
 * Replaces React Native `Alert` + ImagePicker / DocumentPicker flow.
 */
export async function handleUploadSupportingDocuments(
  docType: SupportingDocumentType,
  propertyId: string,
  files: File[],
  description: string
): Promise<ApiResponse<UploadSupportingDocumentsResponse>> {
  if (!propertyId.trim()) {
    return {
      success: false,
      data: {} as UploadSupportingDocumentsResponse,
      message: "Property is required",
    };
  }
  // if (!files.length) {
  //   return {
  //     success: false,
  //     data: {} as UploadSupportingDocumentsResponse,
  //     message: "No files selected",
  //   };
  // }
  const trimmed = description.trim();
  if (!trimmed) {
    return {
      success: false,
      data: {} as UploadSupportingDocumentsResponse,
      message: "Description is required",
    };
  }
  try {
    const res = await stepsService.uploadSupportingDocuments({
      property_id: propertyId,
      document_type: docType,
      files,
      file_description: trimmed,
    });
    const msg = (res.message || "").toLowerCase();
    if (res.success || msg.includes("success")) {
      invalidateSupportingDocumentsGetCacheForProperty(propertyId);
    }
    return res;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Upload failed";
    return {
      success: false,
      data: {} as UploadSupportingDocumentsResponse,
      message,
    };
  }
}

export async function handleGetSupportingDocuments(
  userId: string,
  docType: SupportingDocumentType,
  propertyId: string,
  options?: { bypassCache?: boolean }
): Promise<ApiResponse<SupportingDocumentItem[]>> {
  if (!options?.bypassCache && userId.trim() && propertyId.trim()) {
    const key = supportingListCacheKey(userId, propertyId, docType);
    const hit = supportingListCache.get(key);
    if (hit && Date.now() - hit.at < SUPPORTING_LIST_CACHE_TTL_MS) {
      return hit.response;
    }
  }
  try {
    const res = await stepsService.getSupportingDocuments(userId, docType, propertyId);
    if (userId.trim() && propertyId.trim()) {
      const key = supportingListCacheKey(userId, propertyId, docType);
      supportingListCache.set(key, { at: Date.now(), response: res });
    }
    return res;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Failed to load documents";
    console.error("Error getting supporting documents:", e);
    return {
      success: false,
      data: [],
      message,
    };
  }
}

export async function handleDeleteSupportingDocument(
  documentId: string,
  options?: { propertyIdForCache?: string }
): Promise<ApiResponse<unknown>> {
  try {
    const res = await stepsService.deleteSupportingDocument(documentId);
    if (res.success && options?.propertyIdForCache?.trim()) {
      invalidateSupportingDocumentsGetCacheForProperty(options.propertyIdForCache);
    }
    return res;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : "Delete failed";
    console.error("Error deleting supporting document:", e);
    return {
      success: false,
      data: null,
      message,
    };
  }
}
